(ns hai.home.mobile.mini-apps.notes
  "My Notes mini-app card for the mobile dashboard. Inline-edits the scratchpad
   note loaded by [[hai.notes.stacks]] (auto-saving text, tag/link/contact
   sections always editable). The header's title links to the full notes page; the ＋ archives the
   note and starts a fresh one; the ⋮ holds Delete; the ⤢ opens a full-screen
   editor and the ➤ opens the Sharing dialog. Shares the note editor
   ([[hai.notes.form]]) and the footer/share dialog ([[hai.notes.card]]) with
   the desktop card."
  (:require
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.notes.card :as note-card]
   [hai.notes.editor :as editor]
   [hai.notes.form :as form]
   [hai.notes.kebab :as kebab]
   [hai.notes.sections :as sections]
   [hai.notes.stacks :as stacks]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private expand-icon (icons/mui-icon :outlined :open_in_full))
(def ^:private share-icon (icons/mui-icon :outlined :person_add))

(o/defstyled card mini-apps/card
  {:background t/--sticky-yellow-light}
  ;; Perforated edge under the tinted header, like a notepad.
  [:.app-header
   {:background t/--notes
    :border-bottom-width "2px"
    :border-bottom-style "dotted"
    :border-bottom-color t/--note-sage-35}]
  ;; The header's ⋮ matches the round header buttons; like them, hover tint only
  ;; where hover exists (a tap's sticky :hover would leave it tinted).
  [:.app-actions
   [sections/kebab-wrap
    [:button
     {:width "32px" :height "32px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background "transparent" :color t/--note-kraft}]]
    [:at-media {:hover "hover"}
     [:button [:&:hover {:background t/--note-kraft-10}]]]]])

(o/defstyled note-overview :div
  {:padding "16px"
   :height "240px"
   :position "relative"
   :display "flex" :flex-direction "column" :gap "12px"}
  ;; Expand-to-full-screen, tucked into the text box's top-right corner. Always
  ;; visible on mobile — there's no hover to reveal it.
  [:.note-expand
   {:position "absolute"
    :top "5px" :right "5px"
    :width "32px" :height "32px"
    :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
    :border-radius "8px"
    :background t/--card-surface
    :color t/--note-kraft
    :cursor "pointer"
    :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"}
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]]
  ;; Paper-airplane share, bottom-right corner, always visible.
  [:.note-share
   {:position "absolute"
    :bottom "16px" :right "16px"
    :width "32px" :height "32px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :color t/--note-kraft
    :cursor "pointer"
    :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :z-index "2"}
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]]
  ;; The card is a fixed height. The textarea flexes to fill whatever the
  ;; tags/links rows leave, so revealing the edit controls on focus shrinks the
  ;; writing area (which scrolls) instead of growing the card.
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :outline "none" :resize "none"
    :border "1px solid transparent" :border-radius "8px"
    :padding "8px 10px" :margin "0"
    :font-family "inherit" :font-size "14px" :font-weight "500" :line-height "1.5"
    :color t/--charcoal
    :flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :scrollbar-width "thin"
    :scrollbar-color "rgba(74,93,63,0.55) transparent"
    :transition "border-color 0.15s ease"}
   [:&:focus {:border-color "rgba(74,93,63,0.25)"}]
   [:&::placeholder {:color t/--note-sage-55}]]
  ;; Keep the tag/link chips clear of the share button in the corner.
  [#{sections/extras-wrap sections/add-section-row}
   {:flex "0 0 auto" :padding-right "40px"}])

(defn- note-body [{:keys [!autosave]}]
  (r/with-let [!editor-open? (r/atom false)
               !share-open?  (r/atom false)
               delete!       (fn []
                               (reset! !editor-open? false)
                               (stacks/delete-note! !autosave))]
    (let [loaded?      @(<s [::stacks/loaded?])
          note         @(<s [::stacks/note])
          tags         @(<s [::stacks/tags])
          link-urls    @(<s [::stacks/link-urls])
          contacts     @(<s [::stacks/contacts])
          linked-notes @(<s [::stacks/linked-notes])
          linked-tasks @(<s [::stacks/linked-tasks])
          generation   @(<s [::stacks/generation])
          saving?      @(<s [::stacks/saving?])
          editor-open? @!editor-open?]
      (when loaded?
        [:<>
         [note-overview
          [:button.note-expand
           {:type "button"
            :aria-label "Edit in full screen"
            :on-click #(reset! !editor-open? true)}
           [expand-icon]]
          ;; Remount (re-seeds the uncontrolled textarea) when the full-screen
          ;; editor closes, so it picks up edits made there, and when ＋ starts a
          ;; fresh note (::new-note bumps the generation), so the pad clears.
          ^{:key [editor-open? generation]}
          [form/note-form
           {:form-id      (stacks/scratchpad-form-id generation)
            :note         note
            :resume-text? true
            :editing?     true
            :!autosave    !autosave
            :placeholder  "Jot anything down… saves as you type."
            :on-save-text (fn [text] (>e [::stacks/save {:text text}]))}]
          [form/note-extras
           (merge stacks/extras-handlers
                  {:editing?     true
                   :tags         tags
                   :link-urls    link-urls
                   :contacts     contacts
                   :linked-notes linked-notes
                   :linked-tasks linked-tasks
                   :note-uuid    (:note/uuid note)})]
          (when (:note/uuid note)
            [:button.note-share
             {:type "button"
              :aria-label "Share this note"
              :on-click #(reset! !share-open? true)}
             [share-icon]])]
         (when (:note/uuid note)
           [note-card/share-dialog {:note      note
                                    :!open?    !share-open?
                                    :on-closed #(>e [::stacks/refresh])}])
         (when editor-open?
           [editor/screen
            {:title           "Editing note"
             :note            note
             :tags            tags
             :link-urls       link-urls
             :contacts        contacts
             :linked-notes    linked-notes
             :linked-tasks    linked-tasks
             :saving?         saving?
             :form-id         (stacks/scratchpad-form-id generation)
             :on-save-text    (fn [text] (>e [::stacks/save {:text text}]))
             :extras-handlers stacks/extras-handlers
             :on-share-closed #(>e [::stacks/refresh])
             :on-close        #(reset! !editor-open? false)
             :on-delete       delete!}])]))))

(defn panel []
  (r/with-let [!autosave (atom nil)]
    (let [note @(<s [::stacks/note])]
      [card
       [:div.app-header
        [:a.app-title-link
         {:href (routing/href :notes/view)
          :on-click (fn [e]
                      (.preventDefault e)
                      (>e [:routing/navigate [:notes/view]]))}
         [:span.app-icon [icons/note {:size 18}]]
         [:h2.app-title "My Notes"]]
        [:div.app-actions
         [:button.app-action-btn
          {:type "button"
           :aria-label "Save this note and get a new one"
           :title "Save this note and get a new one"
           :on-click (fn []
                       ;; The archive files the live form text itself; drop the
                       ;; pending autosave so the remount flush can't re-save the
                       ;; text onto the fresh pad.
                       (when-let [{:keys [discard!]} @!autosave]
                         (discard!))
                       (>e [::stacks/new-note]))}
          [icons/plus {:size 16}]]
         (when (:note/uuid note)
           [kebab/menu
            {:on-delete      #(stacks/delete-note! !autosave)
             :delete-label   "Delete note"
             :confirm-prompt "Delete this note?"}])]]
       [note-body {:!autosave !autosave}]])))
